import type { EditableProgram, ProgramUpdatePayload } from './useProgram';

// One value shape for every program form: the edit sections each show and send their own slice of
// it, and the New Program page shows the create-time slices together and sends them at once.
export interface ProgramFormValues {
  additional_copyrights: string[];
  archived: boolean;
  available_to_add: boolean;
  code: string;
  color: string;
  copyright: string;
  description_content: string;
  edition_en: string;
  edition_es: string;
  font_size: string;
  grade: number | null;
  has_narration: boolean;
  has_print_book: boolean;
  homeschool_email: string;
  integrated: boolean;
  license_ids: number[];
  materials_kit_order_url: string;
  notebook_border_color: string;
  order_url: string;
  refill_kit_order_url: string;
  retirement_year: string;
  science: boolean;
  show_online_assessment: boolean;
  show_section_titles: boolean;
  show_source: boolean;
  subtitle: string;
  subtitle_es: string;
  title: string;
  title_es: string;
}

// The programs table's column defaults, for a program that doesn't exist yet.
export const NEW_PROGRAM_VALUES: ProgramFormValues = {
  additional_copyrights: [],
  archived: false,
  available_to_add: false,
  code: '',
  color: '',
  copyright: '',
  description_content: '',
  edition_en: '',
  edition_es: '',
  font_size: '',
  grade: null,
  has_narration: false,
  has_print_book: false,
  homeschool_email: '',
  integrated: false,
  license_ids: [],
  materials_kit_order_url: '',
  notebook_border_color: 'Default (Green)',
  order_url: '',
  refill_kit_order_url: '',
  retirement_year: '',
  science: false,
  show_online_assessment: true,
  show_section_titles: false,
  show_source: true,
  subtitle: '',
  subtitle_es: '',
  title: '',
  title_es: '',
};

export const toFormValues = (program: EditableProgram): ProgramFormValues => ({
  additional_copyrights: program.additional_copyrights,
  archived: program.archived,
  available_to_add: program.available_to_add,
  code: program.code,
  color: program.color ?? '',
  copyright: program.copyright ?? '',
  description_content: program.description_content ?? '',
  edition_en: program.edition_en ?? '',
  edition_es: program.edition_es ?? '',
  font_size: program.font_size ?? '',
  grade: program.grade,
  has_narration: program.has_narration,
  has_print_book: program.has_print_book,
  homeschool_email: program.homeschool_email ?? '',
  integrated: program.integrated,
  license_ids: program.licenses.map(license => license.id),
  materials_kit_order_url: program.materials_kit_order_url ?? '',
  notebook_border_color: program.notebook_border_color,
  order_url: program.order_url ?? '',
  refill_kit_order_url: program.refill_kit_order_url ?? '',
  retirement_year: program.retirement_year === null ? '' : String(program.retirement_year),
  science: program.science,
  show_online_assessment: program.show_online_assessment,
  show_section_titles: program.show_section_titles,
  show_source: program.show_source,
  subtitle: program.subtitle ?? '',
  subtitle_es: program.subtitle_es ?? '',
  title: program.title,
  title_es: program.title_es ?? '',
});

export const informationPayload = (values: ProgramFormValues): ProgramUpdatePayload => ({
  additional_copyrights: values.additional_copyrights,
  available_to_add: values.available_to_add,
  code: values.code,
  color: values.color,
  copyright: values.copyright,
  description_content: values.description_content,
  edition_en: values.edition_en,
  edition_es: values.edition_es,
  grade: values.grade,
  homeschool_email: values.homeschool_email,
  license_ids: values.license_ids,
  retirement_year: values.retirement_year.trim() === '' ? null : Number(values.retirement_year),
  subtitle: values.subtitle,
  subtitle_es: values.subtitle_es,
  title: values.title,
  title_es: values.title_es,
});

// The legacy form only renders some settings for custom programs, so only those reach the server;
// a hidden field keeps its stored value.
export const settingsPayload = (values: ProgramFormValues, custom: boolean): ProgramUpdatePayload => ({
  archived: values.archived,
  font_size: values.font_size,
  has_narration: values.has_narration,
  has_print_book: values.has_print_book,
  notebook_border_color: values.notebook_border_color,
  science: values.science,
  show_online_assessment: values.show_online_assessment,
  show_section_titles: values.show_section_titles,
  ...(custom && values.science ? { integrated: values.integrated } : {}),
  ...(custom ? { show_source: values.show_source } : {}),
});

export const storeOrderUrlsPayload = (values: ProgramFormValues): ProgramUpdatePayload => ({
  materials_kit_order_url: values.materials_kit_order_url,
  order_url: values.order_url,
  refill_kit_order_url: values.refill_kit_order_url,
});
